if (!customElements.get('variant-quantity-picker')) {
  customElements.define(
    'variant-quantity-picker',
    class VariantQuantityPicker extends HTMLElement {
      constructor() {
        super();

        this.inputs = [...this.querySelectorAll('[data-quantity-input]')];
        this.addButton = this.querySelector('[data-add-selected]');
        this.summary = this.querySelector('[data-summary]');
        this.spinner = this.querySelector('.loading__spinner');
        this.addButtonText = this.querySelector('[data-add-selected-text]');
        this.errorWrapper = this.querySelector('[data-error-wrapper]');
        this.errorMessage = this.querySelector('[data-error-message]');

        this.handleClick = this.handleClick.bind(this);
        this.handleInput = this.handleInput.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);

        this.inputs.forEach((input) => {
          input.addEventListener('input', this.handleInput);
          input.addEventListener('change', this.handleInput);
        });

        this.addButton?.addEventListener('click', this.handleSubmit);

        this.updateState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);

        this.inputs.forEach((input) => {
          input.removeEventListener('input', this.handleInput);
          input.removeEventListener('change', this.handleInput);
        });

        this.addButton?.removeEventListener('click', this.handleSubmit);
      }

      handleClick(event) {
        const plusButton = event.target.closest('[data-quantity-plus]');
        const minusButton = event.target.closest('[data-quantity-minus]');

        if (!plusButton && !minusButton) return;

        const wrapper = event.target.closest('[data-quantity-wrapper]');
        if (!wrapper) return;

        const input = wrapper.querySelector('[data-quantity-input]');
        if (!input || input.disabled) return;

        let value = parseInt(input.value || '0', 10);
        if (Number.isNaN(value) || value < 0) value = 0;

        if (plusButton) value += 1;
        if (minusButton) value = Math.max(0, value - 1);

        input.value = value;
        this.updateState();
      }

      handleInput(event) {
        const input = event.target;
        let value = parseInt(input.value || '0', 10);

        if (Number.isNaN(value) || value < 0) value = 0;

        input.value = value;
        this.updateState();
      }

      getItems() {
        return this.inputs
          .map((input) => ({
            id: parseInt(input.dataset.variantId, 10),
            quantity: parseInt(input.value || '0', 10) || 0,
          }))
          .filter((item) => item.quantity > 0);
      }

      updateState() {
        const items = this.getItems();
        const total = items.reduce((sum, item) => sum + item.quantity, 0);

        if (this.summary) {
          this.summary.textContent =
            total === 0 ? '0 ausgewählt' : `${total} ausgewählt`;
        }

        if (this.addButton) {
          this.addButton.disabled = total === 0;
        }

        this.setError('');
      }

      setLoading(isLoading) {
        if (!this.addButton) return;

        this.addButton.disabled = isLoading || this.getItems().length === 0;
        this.addButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');

        if (this.spinner) {
          this.spinner.classList.toggle('hidden', !isLoading);
        }

        if (this.addButtonText) {
          this.addButtonText.classList.toggle('hidden', isLoading);
        }
      }

      setError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;

        if (!message) {
          this.errorWrapper.hidden = true;
          this.errorMessage.textContent = '';
          return;
        }

        this.errorWrapper.hidden = false;
        this.errorMessage.textContent = message;
      }

      async refreshCartUI() {
        const cartDrawerElement = document.querySelector('cart-drawer');
        const hasCartDrawer = !!cartDrawerElement;

        const sectionIds = ['cart-icon-bubble'];
        if (hasCartDrawer) sectionIds.unshift('cart-drawer');

        const url = `${window.Shopify.routes.root}?sections=${sectionIds.join(',')}`;
        const response = await fetch(url, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Cart-UI konnte nicht aktualisiert werden.');
        }

        const sections = await response.json();

        // Cart bubble aktualisieren
        if (sections['cart-icon-bubble']) {
          const bubbleContainer = document.getElementById('cart-icon-bubble');
          if (bubbleContainer) {
            const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const newBubble = doc.getElementById('cart-icon-bubble');

            if (newBubble) {
              bubbleContainer.innerHTML = newBubble.innerHTML;
            }
          }
        }

        // Cart drawer aktualisieren
        if (hasCartDrawer && sections['cart-drawer']) {
          const parser = new DOMParser();
          const drawerDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newCartDrawer = drawerDoc.querySelector('cart-drawer');
          const currentCartDrawer = document.querySelector('cart-drawer');

          if (newCartDrawer && currentCartDrawer) {
            currentCartDrawer.replaceWith(newCartDrawer);

            // Nach dem Replace erneut referenzieren
            const replacedDrawer = document.querySelector('cart-drawer');

            if (replacedDrawer) {
              replacedDrawer.classList.remove('is-empty');

              if (typeof replacedDrawer.open === 'function') {
                replacedDrawer.open();
              }
            }
          }
        }
      }

      async handleSubmit() {
        const items = this.getItems();
        if (!items.length) return;

        this.setLoading(true);
        this.setError('');

        try {
          const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });

          const addData = await addResponse.json();

          if (!addResponse.ok) {
            throw new Error(
              addData.description ||
              addData.message ||
              'Die ausgewählten Varianten konnten nicht hinzugefügt werden.'
            );
          }

          await this.refreshCartUI();

          this.inputs.forEach((input) => {
            if (!input.disabled) input.value = 0;
          });

          this.updateState();
        } catch (error) {
          this.setError(
            error.message || 'Die ausgewählten Varianten konnten nicht hinzugefügt werden.'
          );
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}